import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { ExamPreparationSubject } from '@chancenwerk/enums/enums.js';
import { Bundesland, type Weekday } from '@chancenwerk/enums/enums.js';
import { DateTime } from 'luxon';
import { Course } from './Course.js';

/**
 * @entity CourseExamPreparation
 */
@Entity()
export class CourseExamPreparation extends Course {
  public static override _class = 'CourseExamPreparation';

  @Field(() => String)
  public subject!: ExamPreparationSubject;

  // Course has weekday, startTime, endTime we use for weekly time slot
  // Everything else we won't be using (refusing the bequest) but this is just due to uninhibited usage of inheritance.

  // This is just for future extensibility and, for now, is always NRW.
  @Field(() => String)
  public bundesland: Maybe<Bundesland> = Bundesland.NORDRHEINWESTFALEN;
}

export const createExamPreparationCourses = registerMutation({
  type: () => CourseExamPreparation,
  operationName: 'createExamPreparationCourses',
  options: {},
  params: [
    { name: 'subjects', type: () => [String] },
    { name: 'weekdays', type: () => [String] },
    { name: 'startTime', type: () => DateTime },
    { name: 'endTime', type: () => DateTime },
  ],
  resolve:
    (subjects: ExamPreparationSubject[], weekdays: Weekday[], startTime: DateTime, endTime: DateTime) => async () => {
      const hStartTime = startTime.set({ year: 1970, month: 1, day: 1, second: 0, millisecond: 0 });
      const hEndTime = endTime.set({ year: 1970, month: 1, day: 1, second: 0, millisecond: 0 });
      const collection = await getRawCollection<EntityData<CourseExamPreparation>>(
        CourseExamPreparation._collectionName,
      );
      const existingDocuments = await collection
        .aggregate<CourseExamPreparation>([
          { $match: { _class: CourseExamPreparation._class } },
          {
            // Filter out some irrelevant entries right here
            $match: { subject: { $in: subjects }, weekday: { $in: weekdays } },
          },
        ])
        .map(d => constructValue<CourseExamPreparation>(d))
        .toArray();
      const documents = subjects
        .flatMap(subject => weekdays.map(weekday => [subject, weekday] as [ExamPreparationSubject, Weekday]))
        .map(([subject, weekday]) => ({ subject, weekday, startTime: hStartTime, endTime: hEndTime }))
        .filter(
          doc =>
            !existingDocuments.some(existing => {
              return (
                existing.subject === doc.subject &&
                existing.weekday === doc.weekday &&
                existing.startTime?.toFormat('HH:mm') === doc.startTime?.toFormat('HH:mm') &&
                existing.endTime?.toFormat('HH:mm') === doc.endTime?.toFormat('HH:mm')
              );
            }),
        )
        .map(doc => new CourseExamPreparation(doc));
      collection
        .insertMany(documents.map(d => d.toJSON()))
        .catch(err => logger.error(err, 'Error inserting exam prep documents in database'));
    },
});
